// Bills.js - Complete Unified Component
import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  FaMoneyBillWave, FaPlusCircle, FaEye, FaEdit, FaChevronLeft, FaChevronRight,
  FaRegComment, FaCheckCircle, FaClock, FaFileInvoice, FaTimes, FaSave,
  FaCalendarAlt, FaSearch, FaPaperclip, FaCheck
} from "react-icons/fa";
import { FaReceipt } from "react-icons/fa";
import { RiDeleteBinFill, RiDeleteBin5Fill } from "react-icons/ri";
import { MdOutlineArrowDropDown } from "react-icons/md";
import Swal from 'sweetalert2';
import './Bills.css';

// ✅ Import Autofill Component
import AutofillBill from './AutofillBill';

// ============================================================
// API HELPERS
// ============================================================
const API_BASE = 'https://super-backend.hira-tech.co.uk/api';
const BACKEND_BASE_URL = 'https://super-backend.hira-tech.co.uk'; // ✅ Added for images/media

const getAuthHeaders = () => {
  const token = localStorage.getItem('access_token');
  const shopId =
    localStorage.getItem('selectedShopId') ||
    localStorage.getItem('selected_shop_id') ||
    '';
  return {
    Authorization: token ? `Bearer ${token}` : '',
    'X-Shop-Id': shopId,
    'Accept': 'application/json',
  };
};

const buildBillPayload = (form, isEdit) => {
  const firstLine = form.lines[0] || {};
  const payload = {
    supplier: form.supplier || '',
    bill_date: form.billDate || new Date().toISOString().split('T')[0],
    due_date: form.dueDate || null,
    memo: form.memo || '',
    shop: form.shop ? parseInt(form.shop, 10) : null,
    city: form.city || null,
    status: 'unpaid', // Bill status
    items: form.lines.map((line, index) => ({
      rowNumber: index + 1,
      category: line.category || 'General',
      description: line.description || '',
      totalAmount: parseFloat(line.amount) || 0,
    })),
    category: firstLine.category || null,
    source: 'Manual'
  };
  return payload;
};

const buildFormDataFromPayload = (payload, file) => {
  const fd = new FormData();
  Object.entries(payload).forEach(([key, value]) => {
    if (key === 'items') {
      fd.append('items', JSON.stringify(value));
    } else if (value !== null && value !== undefined && value !== '') {
      fd.append(key, value);
    }
  });
  if (file) fd.append('file', file);
  return fd;
};

const normalizeSupplier = (s) => {
  if (s == null) return null;
  if (typeof s === 'string') return { value: s, label: s };
  const label =
    s.supplier ||
    s.supplier_display_name ||
    s.company_name ||
    s.name ||
    'N/A';
  const value = s.id ?? label;
  return { value, label };
};

// Map backend Bill row -> frontend row shape
const mapBillFromBackend = (r) => {
  const formattedAmount = r.amount ? `£${parseFloat(r.amount).toFixed(2)}` : '£0.00';
  return {
    id: r.id,
    bill: 'Bill',
    source: r.source || 'Manual',
    supplier: r.supplier || r.payee || '-',
    billNo: r.bill_number || '',
    billDate: r.bill_date || '',
    category: r.category || 'General',
    dueDate: r.due_date || '',
    billAmount: r.amount ? parseFloat(r.amount) : 0,
    billAmountFormatted: formattedAmount,
    status: (r.status || 'unpaid').toLowerCase(),
    attachmentName: r.file_path || '',
    attachmentUrl: r.file_path || '',
    rawFormData: r,
  };
};

const Bills = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [activeRegion, setActiveRegion] = useState(() => {
    if (location.pathname.endsWith('/pk')) return 'pk';
    return 'uk';
  });

  useEffect(() => {
    if (location.pathname.endsWith('/pk')) setActiveRegion('pk');
    else setActiveRegion('uk');
  }, [location.pathname]);

  const [billsData, setBillsData] = useState([]);
  const [billsLoading, setBillsLoading] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const triggerReload = () => setReloadKey((k) => k + 1);

  const [showPayBillsDropdown, setShowPayBillsDropdown] = useState(false);
  const [showNewBillDropdown, setShowNewBillDropdown] = useState(false);
  const [showBatchActionsDropdown, setShowBatchActionsDropdown] = useState(false);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedBill, setSelectedBill] = useState(null);
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [selectedDateRange, setSelectedDateRange] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [showDateDropdown, setShowDateDropdown] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedBills, setSelectedBills] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  const [filterDates, setFilterDates] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [amountRange, setAmountRange] = useState('between');
  const [minAmount, setMinAmount] = useState('');
  const [maxAmount, setMaxAmount] = useState('');

  const [showNewBillModal, setShowNewBillModal] = useState(false);

  // ✅ State for Autofill Modal
  const [showAutofillModal, setShowAutofillModal] = useState(false);

  const [saving, setSaving] = useState(false);
  const [editingRowId, setEditingRowId] = useState(null);
  const [editingLineId, setEditingLineId] = useState(null);
  const [editFormData, setEditFormData] = useState({ category: '', description: '', amount: '' });
  const [billForm, setBillForm] = useState({
    supplier: '',
    shop: '',
    city: '',
    billDate: '',
    dueDate: '',
    billNo: '',
    memo: '',
    attachment: null,
    attachmentName: '',
    attachmentUrl: '',
    lines: [
      { id: 1, category: '', description: '', amount: '' },
      { id: 2, category: '', description: '', amount: '' },
    ],
  });
  const [suppliersList, setSuppliersList] = useState([]);
  const [suppliersLoading, setSuppliersLoading] = useState(false);
  const [shopsList, setShopsList] = useState([]);
  const [citiesList, setCitiesList] = useState([]);
  const [citiesLoading, setCitiesLoading] = useState(false);
  const billFileInputRef = useRef(null);
  const billModalRef = useRef(null);

  const [billTypes, setBillTypes] = useState([]);

  useEffect(() => {
    let cancelled = false;
    const fetchBills = async () => {
      setBillsLoading(true);
      try {
        const shopId = localStorage.getItem('selectedShopId') || localStorage.getItem('selected_shop_id');
        const qs = shopId ? `?shop_id=${shopId}&page_size=200` : '?page_size=200';
        const res = await fetch(`${API_BASE}/bills/${qs}`, { headers: getAuthHeaders() });

        if (!res.ok) throw new Error('Failed to load bills');
        const data = await res.json();

        let rows = Array.isArray(data) ? data : data.results || [];

        if (!cancelled) {
          setBillsData(rows.map(mapBillFromBackend));
        }
      } catch (err) {
        console.error('Fetch bills error:', err);
        if (!cancelled) {
          Swal.fire({ icon: 'error', title: 'Error', text: err.message });
        }
      } finally {
        if (!cancelled) setBillsLoading(false);
      }
    };
    fetchBills();
    return () => { cancelled = true; };
  }, [reloadKey, activeRegion]);

  useEffect(() => {
    setSelectedBills([]);
    setSelectedStatus('all');
    setSelectedFilter('all');
    setSelectedDateRange('all');
    setSearchTerm('');
  }, [activeRegion]);

  const payBillsRef = useRef(null);
  const newBillRef = useRef(null);
  const batchActionsRef = useRef(null);
  const payBillsBtnRef = useRef(null);
  const newBillBtnRef = useRef(null);
  const batchActionsBtnRef = useRef(null);
  const feedbackBtnRef = useRef(null);
  const filterBtnRef = useRef(null);
  const filterModalRef = useRef(null);
  const dateBtnRef = useRef(null);
  const dateDropdownRef = useRef(null);
  const viewModalRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (payBillsBtnRef.current && !payBillsBtnRef.current.contains(event.target) &&
          payBillsRef.current && !payBillsRef.current.contains(event.target)) {
        setShowPayBillsDropdown(false);
      }
      if (newBillBtnRef.current && !newBillBtnRef.current.contains(event.target) &&
          newBillRef.current && !newBillRef.current.contains(event.target)) {
        setShowNewBillDropdown(false);
      }
      if (batchActionsBtnRef.current && !batchActionsBtnRef.current.contains(event.target) &&
          batchActionsRef.current && !batchActionsRef.current.contains(event.target)) {
        setShowBatchActionsDropdown(false);
      }
      if (dateBtnRef.current && !dateBtnRef.current.contains(event.target) &&
          dateDropdownRef.current && !dateDropdownRef.current.contains(event.target)) {
        setShowDateDropdown(false);
      }
      if (viewModalRef.current && !viewModalRef.current.contains(event.target) && showViewModal) {
        setShowViewModal(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showViewModal]);

  useEffect(() => {
    const fetchBillTypes = async () => {
      try {
        const res = await fetch(`${API_BASE}/bills/new-bill-options/`, {
          headers: getAuthHeaders(),
        });
        if (res.ok) {
          const data = await res.json();
          setBillTypes(Array.isArray(data) ? data : []);
        }
      } catch (err) {
        console.error('Bill types fetch error:', err);
        setBillTypes([
          { name: 'Upload from computer', route: '/bill/upload' },
          { name: 'Create bill', route: 'bill-modal' },
          { name: 'Create recurring bill', route: '/recurring-bill/create' },
        ]);
      }
    };
    fetchBillTypes();
  }, []);

  useEffect(() => {
    if (!showNewBillModal) return;
    const fetchSuppliers = async () => {
      setSuppliersLoading(true);
      try {
        const shopId =
          localStorage.getItem('selectedShopId') ||
          localStorage.getItem('selected_shop_id') ||
          '';
        const qs = shopId ? `?shop_id=${encodeURIComponent(shopId)}` : '';
        const res = await fetch(`${API_BASE}/suppliers/${qs}`, {
          headers: getAuthHeaders(),
        });
        if (!res.ok) throw new Error('Failed to load suppliers');
        const data = await res.json();
        const list = Array.isArray(data)
          ? data
          : data.results || data.data || data.suppliers || [];
        setSuppliersList(list);
      } catch (err) {
        console.error('Supplier fetch error:', err);
        setSuppliersList([]);
      } finally {
        setSuppliersLoading(false);
      }
    };
    fetchSuppliers();
  }, [showNewBillModal]);

  useEffect(() => {
    const fetchShops = async () => {
      try {
        const res = await fetch(`${API_BASE}/expenses/shops/`, { headers: getAuthHeaders() });
        if (res.ok) {
          const data = await res.json();
          setShopsList(Array.isArray(data) ? data : data.results || []);
        }
      } catch (err) {
        console.error("Shops fetch error:", err);
      }
    };
    fetchShops();
  }, []);

  const fetchCitiesForShop = async (shopId) => {
    if (!shopId) { setCitiesList([]); return; }
    setCitiesLoading(true);
    try {
      const res = await fetch(`${API_BASE}/expenses/shop/${shopId}/cities/`, { headers: getAuthHeaders() });
      if (res.ok) {
        const data = await res.json();
        setCitiesList(Array.isArray(data) ? data : data.results || []);
      }
    } catch (err) {
      console.error("Cities fetch error:", err);
    } finally {
      setCitiesLoading(false);
    }
  };

  const handleFeedback = () => navigate('/feedback');

  const handlePayBills = () => {
    setShowPayBillsDropdown(!showPayBillsDropdown);
    setShowNewBillDropdown(false);
    setShowBatchActionsDropdown(false);
  };

  const handleNewBill = () => {
    setShowNewBillDropdown(!showNewBillDropdown);
    setShowPayBillsDropdown(false);
    setShowBatchActionsDropdown(false);
  };

  const handleBatchActions = () => {
    setShowBatchActionsDropdown(!showBatchActionsDropdown);
    setShowPayBillsDropdown(false);
    setShowNewBillDropdown(false);
  };

  const handleMakePayment = () => {
    if (selectedBills.length === 0) {
      alert('Please select at least one bill to pay');
      return;
    }
    alert(`Pay ${selectedBills.length} selected bill(s)`);
    setShowPayBillsDropdown(false);
  };

  const handleConfirmSelected = () => {
    if (selectedBills.length === 0) {
      alert('Please select at least one bill to confirm');
      return;
    }
    alert(`Confirmed ${selectedBills.length} selected bill(s)`);
    setShowBatchActionsDropdown(false);
  };

  const handleDeleteSelected = () => {
    if (selectedBills.length === 0) {
      alert('Please select at least one bill to delete');
      return;
    }
    if (window.confirm(`Are you sure you want to delete ${selectedBills.length} selected bill(s)?`)) {
      const updatedBills = billsData.filter(bill => !selectedBills.includes(bill.id));
      setBillsData(updatedBills);
      setSelectedBills([]);
      alert(`Deleted ${selectedBills.length} selected bill(s)`);
    }
    setShowBatchActionsDropdown(false);
  };

  // ✅ FIXED: Handle Upload route to show Autofill Modal
  const handleBillType = (type, route) => {
    setShowNewBillDropdown(false);
    if (route === 'bill-modal') {
      setEditingRowId(null);
      resetBillForm();
      setShowNewBillModal(true);
    } else if (route === '/bill/upload') {
      // ✅ Open the Autofill modal
      setShowAutofillModal(true);
    } else if (route) {
      navigate(route);
    } else {
      alert(`Create new ${type}`);
    }
  };

  const resetBillForm = () => {
    setBillForm({
      supplier: '', shop: '', city: '', billDate: '', dueDate: '', billNo: '', memo: '',
      attachment: null, attachmentName: '', attachmentUrl: '',
      lines: [{ id: 1, category: '', description: '', amount: '' }, { id: 2, category: '', description: '', amount: '' }],
    });
    setCitiesList([]);
    setEditingLineId(null);
    setEditFormData({ category: '', description: '', amount: '' });
  };

  const closeBillModal = () => {
    setShowNewBillModal(false);
    setEditingRowId(null);
    setEditingLineId(null);
    resetBillForm();
  };

  const handleBillInputChange = (e) => {
    const { name, value } = e.target;
    setBillForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleBillShopChange = (e) => {
    const shopId = e.target.value;
    setBillForm(prev => ({ ...prev, shop: shopId, city: '' }));
    fetchCitiesForShop(shopId);
  };

  const handleBillLineChange = (id, field, value) => {
    setBillForm((prev) => ({
      ...prev,
      lines: prev.lines.map((line) => line.id === id ? { ...line, [field]: value } : line),
    }));
  };

  const addBillLine = () => {
    const newId = billForm.lines.length > 0 ? Math.max(...billForm.lines.map((l) => l.id)) + 1 : 1;
    setBillForm((prev) => ({ ...prev, lines: [...prev.lines, { id: newId, category: '', description: '', amount: '' }] }));
  };

  const removeBillLine = (id) => {
    setBillForm((prev) => ({ ...prev, lines: prev.lines.filter((line) => line.id !== id) }));
  };

  const clearAllBillLines = () => setBillForm((prev) => ({ ...prev, lines: [] }));

  const calculateBillTotal = () => {
    return billForm.lines.reduce((sum, line) => sum + (parseFloat(line.amount) || 0), 0).toFixed(2);
  };

  const handleBillAttachmentChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setBillForm((prev) => ({
        ...prev,
        attachment: file,
        attachmentName: file.name,
        attachmentUrl: URL.createObjectURL(file)
      }));
    }
  };

  const handleStartEdit = (line) => {
    setEditingLineId(line.id);
    setEditFormData({ category: line.category, description: line.description, amount: line.amount });
  };

  const handleCancelEdit = () => {
    setEditingLineId(null);
    setEditFormData({ category: '', description: '', amount: '' });
  };

  const handleSaveEdit = (id) => {
    setBillForm((prev) => ({
      ...prev,
      lines: prev.lines.map((line) => line.id === id ? { ...line, ...editFormData } : line),
    }));
    setEditingLineId(null);
    setEditFormData({ category: '', description: '', amount: '' });
  };

  const handleSaveBill = async () => {
    if (!billForm.supplier) {
      Swal.fire({ icon: 'warning', title: 'Missing Info', text: 'Please select a supplier.', confirmButtonColor: '#004aad' });
      return;
    }
    setSaving(true);
    try {
      const isEdit = !!editingRowId;
      const payload = buildBillPayload(billForm, isEdit);
      const fd = buildFormDataFromPayload(payload, billForm.attachment);

      const url = isEdit ? `${API_BASE}/bills/${editingRowId}/` : `${API_BASE}/bills/`;

      const res = await fetch(url, {
        method: isEdit ? 'PATCH' : 'POST',
        headers: getAuthHeaders(),
        body: fd,
      });

      if (!res.ok) {
        const errBody = await res.json().catch(() => ({}));
        throw new Error(
          errBody.detail ||
          (errBody.items && JSON.stringify(errBody.items)) ||
          (errBody.city && JSON.stringify(errBody.city)) ||
          'Save failed'
        );
      }

      Swal.fire({
        icon: 'success',
        title: isEdit ? 'Updated!' : 'Saved!',
        text: isEdit ? 'Bill updated successfully.' : 'Bill added successfully.',
        timer: 2000,
        showConfirmButton: false
      });

      closeBillModal();
      triggerReload();

    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Error', text: err.message || 'Could not save bill.' });
    } finally {
      setSaving(false);
    }
  };

  const handleSelectBill = (id) => {
    setSelectedBills(prev => prev.includes(id) ? prev.filter(billId => billId !== id) : [...prev, id]);
  };

  const handleSelectAll = () => {
    if (selectedBills.length === filteredData.length) setSelectedBills([]);
    else setSelectedBills(filteredData.map(bill => bill.id));
  };

  const handleViewBill = (bill) => { setSelectedBill(bill); setShowViewModal(true); };

  const handleEditBill = (bill) => {
    setEditingRowId(bill.id);
    const raw = bill.rawFormData || {};

    setBillForm({
      supplier: raw.supplier || bill.supplier || '',
      shop: raw.shop || '',
      city: raw.city || '',
      billDate: raw.bill_date || bill.billDate || '',
      dueDate: raw.due_date || bill.dueDate || '',
      billNo: raw.bill_number || bill.billNo || '',
      memo: raw.memo || '',
      attachment: null,
      attachmentName: bill.attachmentName || '',
      attachmentUrl: bill.attachmentUrl || '',
      lines: (raw.items || []).length > 0
        ? raw.items.map((l, i) => ({
            id: i + 1,
            category: l.category || '',
            description: l.description || '',
            amount: l.totalAmount ? String(l.totalAmount) : ''
          }))
        : [{ id: 1, category: '', description: '', amount: '' }, { id: 2, category: '', description: '', amount: '' }]
    });

    if (raw.shop) fetchCitiesForShop(raw.shop);
    setShowNewBillModal(true);
  };

  const handleDeleteBill = async (bill) => {
    if (window.confirm(`Are you sure you want to delete bill ${bill.billNo}?`)) {
      try {
        const res = await fetch(`${API_BASE}/bills/${bill.id}/`, {
          method: 'DELETE',
          headers: getAuthHeaders(),
        });
        if (!res.ok && res.status !== 204) throw new Error('Delete failed');

        setBillsData(prev => prev.filter(b => b.id !== bill.id));
        alert(`Deleted bill: ${bill.billNo}`);
      } catch (err) {
        Swal.fire({ icon: 'error', title: 'Error', text: err.message });
      }
    }
  };

  const handleApplyFilter = () => {
    setShowFilterModal(false);
    alert('Filter applied!');
  };

  const handleResetFilter = () => {
    setFilterDates('all'); setFilterCategory('all'); setAmountRange('between');
    setMinAmount(''); setMaxAmount(''); setSelectedFilter('all');
    setSelectedDateRange('all'); setSelectedStatus('all'); setSearchTerm('');
    alert('Filters reset!');
  };

  const getFilteredData = () => {
    let filtered = [...billsData];
    if (selectedFilter !== 'all') {
      filtered = filtered.filter(item => item.category.toLowerCase() === selectedFilter.toLowerCase());
    }
    if (selectedStatus !== 'all') {
      filtered = filtered.filter(item => item.status === selectedStatus.toLowerCase());
    }
    const term = searchTerm.trim().toLowerCase();
    if (term) {
      filtered = filtered.filter(item =>
        [item.supplier, item.billNo, item.bill, item.category].some(v => String(v || '').toLowerCase().includes(term))
      );
    }
    return filtered;
  };

  const filteredData = getFilteredData();
  const unpaidCount = billsData.filter(bill => bill.status === 'unpaid').length;
  const paidCount = billsData.filter(bill => bill.status === 'paid').length;
  const currencySymbol = activeRegion === 'uk' ? '£' : '₨';

  const renderBillsTable = () => (
    <table className="bills-table">
      <thead>
        <tr>
          <th className="checkbox-col">
            <input type="checkbox" checked={selectedBills.length === filteredData.length && filteredData.length > 0} onChange={handleSelectAll} className="select-all-checkbox" />
          </th>
          <th>Bill</th>
          <th>Source</th>
          <th>Supplier</th>
          <th>Bill No</th>
          <th>Bill Date</th>
          <th>Category</th>
          <th>Due Date</th>
          <th>Bill Amount</th>
          <th>Action</th>
        </tr>
      </thead>
      <tbody>
        {billsLoading && (
          <tr><td colSpan="10" style={{ textAlign: 'center', padding: '24px' }}>Loading bills...</td></tr>
        )}
        {!billsLoading && filteredData.length === 0 && (
          <tr><td colSpan="10" style={{ textAlign: 'center', padding: '24px' }}>No bills found</td></tr>
        )}
        {!billsLoading && filteredData.map((item) => (
          <tr key={item.id} className={`bill-row ${item.status}`}>
            <td className="checkbox-col">
              <input type="checkbox" checked={selectedBills.includes(item.id)} onChange={() => handleSelectBill(item.id)} className="bill-checkbox" />
            </td>
            <td><div className="bill-type"><FaFileInvoice className="bill-icon" />{item.bill}</div></td>
            <td>{item.source}</td>
            <td>{item.supplier}</td>
            <td>{item.billNo}</td>
            <td>{item.billDate}</td>
            <td>{item.category}</td>
            <td className={new Date(item.dueDate) < new Date() && item.status === 'unpaid' ? 'overdue' : ''}>{item.dueDate}</td>
            <td className="bill-amount">{item.billAmountFormatted}</td>
            <td>
              <div className="action-buttons">
                <button className="icon-action-btn view" onClick={() => handleViewBill(item)} title="View"><FaEye /></button>
                <button className="icon-action-btn edit" onClick={() => handleEditBill(item)} title="Edit"><FaEdit /></button>
                <button className="icon-action-btn delete" onClick={() => handleDeleteBill(item)} title="Delete"><RiDeleteBinFill /></button>
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );

  return (
    <div className="bills-main">
      <div className="bills-content-wrapper">
        <div className="bills-header-section">
          <div className="bills-title-section">
            <h2><FaReceipt className="title-icon" /> Bills</h2>
          </div>
          <div className="bills-action-buttons">
            <div className="button-wrapper">
              <button ref={feedbackBtnRef} className="action-btn feedback-btn" onClick={handleFeedback}><FaRegComment className="btn-icon" />Feedback</button>
            </div>
            <div className="button-wrapper">
              <button ref={payBillsBtnRef} className="action-btn paybills-btn" onClick={handlePayBills}><FaMoneyBillWave className="btn-icon" />Pay bills</button>
              {showPayBillsDropdown && (
                <div className="action-dropdown paybills-dropdown" ref={payBillsRef}>
                  <div className="dropdown-item-action" onClick={handleMakePayment}>
                    <div className="dropdown-item-content"><div className="dropdown-item-title">Make Recurring Payment</div></div>
                  </div>
                </div>
              )}
            </div>
            <div className="button-wrapper">
              <button ref={newBillBtnRef} className="action-btn newbill-btn" onClick={handleNewBill}><FaPlusCircle className="btn-icon" />New Bill</button>
              {showNewBillDropdown && (
                <div className="action-dropdown bill-dropdown" ref={newBillRef}>
                  <div className="bill-list">
                    {billTypes.map((type, index) => (
                      <div key={index} className="bill-list-item" onClick={() => handleBillType(type.name, type.route)}><div className="bill-list-name">{type.name}</div></div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="bills-filter-bar">
          <div className="bills-status-tabs">
            <button className={`status-tab ${selectedStatus === 'all' ? 'active' : ''}`} onClick={() => setSelectedStatus('all')}>All Bills ({billsData.length})</button>
            <button className={`status-tab unpaid ${selectedStatus === 'unpaid' ? 'active' : ''}`} onClick={() => setSelectedStatus('unpaid')}><FaClock className="status-icon" />Unpaid ({unpaidCount})</button>
            <button className={`status-tab paid ${selectedStatus === 'paid' ? 'active' : ''}`} onClick={() => setSelectedStatus('paid')}><FaCheckCircle className="status-icon" />Paid ({paidCount})</button>
          </div>
          <div className="bills-toolbar">
            <div className="date-filter-wrapper">
              <div className="date-filter-btn" ref={dateBtnRef} onClick={() => setShowDateDropdown(!showDateDropdown)}>
                <FaCalendarAlt className="calendar-icon" />
                <span>Dates: {selectedDateRange === 'all' ? 'All Dates' : selectedDateRange === 'today' ? 'Today' : selectedDateRange === 'week' ? 'Last 7 Days' : 'Last 30 Days'}<MdOutlineArrowDropDown className="dropdown-icon" /></span>
              </div>
              {showDateDropdown && (
                <div className="dropdown-menu" ref={dateDropdownRef}>
                  <div className="dropdown-item" onClick={() => { setSelectedDateRange('all'); setShowDateDropdown(false); }}>All Dates</div>
                  <div className="dropdown-item" onClick={() => { setSelectedDateRange('today'); setShowDateDropdown(false); }}>Today</div>
                  <div className="dropdown-item" onClick={() => { setSelectedDateRange('week'); setShowDateDropdown(false); }}>Last 7 Days</div>
                  <div className="dropdown-item" onClick={() => { setSelectedDateRange('month'); setShowDateDropdown(false); }}>Last 30 Days</div>
                </div>
              )}
            </div>
            <div className="filter-actions-wrapper">
              <button ref={filterBtnRef} className="filter-actions-btn" onClick={() => setShowFilterModal(true)}>Filter <MdOutlineArrowDropDown className="dropdown-icon" /></button>
            </div>
            <div className="bills-search">
              <FaSearch className="search-icon" />
              <input type="text" placeholder="Search bills..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
            </div>
            <div className="batch-actions-wrapper">
              <button ref={batchActionsBtnRef} className="batch-actions-btn" onClick={handleBatchActions}>Batch actions <MdOutlineArrowDropDown className="dropdown-icon" /></button>
              {showBatchActionsDropdown && (
                <div className="batch-actions-dropdown" ref={batchActionsRef}>
                  <div className="batch-action-item" onClick={handleConfirmSelected}>Confirm selected</div>
                  <div className="batch-action-item" onClick={handleDeleteSelected}>Delete selected</div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Filter Modal */}
        {showFilterModal && (
          <div className="filter-modal-overlay">
            <div className="filter-modal" ref={filterModalRef}>
              <div className="filter-modal-header">
                <h3>Filter</h3>
                <button className="close-modal" onClick={() => setShowFilterModal(false)}><FaTimes /></button>
              </div>
              <div className="filter-modal-body">
                <div className="filter-section">
                  <label className="filter-label">ACCOUNT/CATEGORY</label>
                  <select className="filter-select" value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)}>
                    <option value="all">Select category</option>
                    <option value="purchase">Purchase</option>
                    <option value="utilities">Utilities</option>
                    <option value="services">Services</option>
                    <option value="import">Import</option>
                  </select>
                </div>
                <div className="filter-section">
                  <label className="filter-label">AMOUNT</label>
                  <div className="amount-row-container">
                    <div className="amount-select-wrapper">
                      <select className="amount-select" value={amountRange} onChange={(e) => setAmountRange(e.target.value)}>
                        <option value="between">Between</option>
                        <option value="equal">Equal to</option>
                        <option value="greater">Greater than</option>
                        <option value="less">Less than</option>
                      </select>
                    </div>
                    <div className="amount-input-wrapper">
                      <input type="number" className="amount-input" placeholder={`Min (${currencySymbol})`} value={minAmount} onChange={(e) => setMinAmount(e.target.value)} />
                    </div>
                    <div className="amount-input-wrapper">
                      <input type="number" className="amount-input" placeholder={`Max (${currencySymbol})`} value={maxAmount} onChange={(e) => setMaxAmount(e.target.value)} />
                    </div>
                  </div>
                </div>
              </div>
              <div className="filter-modal-footer">
                <button className="reset-filter-btn" onClick={handleResetFilter}>Reset</button>
                <button className="apply-filter-btn" onClick={handleApplyFilter}>Apply</button>
              </div>
            </div>
          </div>
        )}

        {/* View Bill Modal (Read Only) */}
        {showViewModal && selectedBill && (
          <div className="modal-overlay">
            <div className="modal-container" ref={viewModalRef}>
              <div className="modal-header">
                <h3>Review Bill</h3>
                <button className="modal-close-btn" onClick={() => setShowViewModal(false)}><FaTimes /></button>
              </div>
              <div className="modal-content">
                <div className="form-section">
                  <h4>Review the details before submitting</h4>
                  <div className="form-grid">
                    <div className="form-group"><label className="form-label">Payee</label><div className="form-value">{selectedBill.supplier}</div></div>
                    <div className="form-row">
                      <div className="form-group"><label className="form-label">Bill Date</label><div className="form-value">{selectedBill.billDate}</div></div>
                      <div className="form-group"><label className="form-label">Due Date</label><div className="form-value">{selectedBill.dueDate}</div></div>
                    </div>
                    <div className="form-group"><label className="form-label">Total Amount</label><div className="form-value amount-value">{selectedBill.billAmountFormatted}</div></div>
                    <div className="form-row">
                      <div className="form-group"><label className="form-label">Account/Category</label><div className="form-value">{selectedBill.category}</div></div>
                    </div>
                    <div className="form-group"><label className="form-label">Bill No</label><div className="form-value">{selectedBill.billNo}</div></div>
                    <div className="form-group"><label className="form-label">Memo</label><div className="form-value memo-value">{selectedBill.rawFormData?.memo || 'No memo added'}</div></div>
                  </div>
                </div>
                <div className="modal-action-buttons">
                  <button className="action-btn-secondary" onClick={() => setShowViewModal(false)}>Cancel</button>
                  <button className="action-btn-primary" onClick={() => { setShowViewModal(false); handleEditBill(selectedBill); }}><FaEdit /> Edit Bill</button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* NEW / EDIT BILL MODAL */}
        {showNewBillModal && (
          <div className="expense-modal-overlay">
            <div className="expense-modal" ref={billModalRef}>
              <div className="expense-modal-header">
                <div className="expense-modal-title"><h3>{editingRowId ? 'Edit Bill' : 'New Bill'}</h3></div>
                <div className="expense-modal-header-right">
                  <button className="modal-close-btn" onClick={closeBillModal}><FaTimes /></button>
                </div>
              </div>

              <div className="expense-balance-bar"><span>BALANCE DUE</span><strong>£0.00</strong></div>

              <div className="expense-modal-body">
                <div className="modal-top-section">
                  <div className="modal-form-row modal-form-row-4">
                    <div className="modal-form-group">
                      <label>Supplier</label>
                      <select name="supplier" value={billForm.supplier} onChange={handleBillInputChange} disabled={suppliersLoading}>
                        <option value="">{suppliersLoading ? 'Loading...' : 'Select supplier'}</option>
                        {suppliersList.length > 0
                          ? suppliersList.map((raw) => {
                              const s = normalizeSupplier(raw);
                              if (!s) return null;
                              return <option key={s.value} value={s.label}>{s.label}</option>;
                            })
                          : (!suppliersLoading && <option value="" disabled>No suppliers available</option>)}
                      </select>
                    </div>
                    <div className="modal-form-group"><label>Bill No.</label><input type="text" name="billNo" value={billForm.billNo} onChange={handleBillInputChange} placeholder="Enter bill number" /></div>
                    <div className="modal-form-group"><label>Bill Date</label><input type="date" name="billDate" value={billForm.billDate} onChange={handleBillInputChange} /></div>
                    <div className="modal-form-group"><label>Due Date</label><input type="date" name="dueDate" value={billForm.dueDate} onChange={handleBillInputChange} /></div>
                  </div>

                  <div className="modal-form-row">
                    <div className="modal-form-group">
                      <label>Shop</label>
                      <select name="shop" value={billForm.shop} onChange={handleBillShopChange}>
                        <option value="">Select a shop</option>
                        {shopsList.map(shop => (
                          <option key={shop.id} value={shop.id}>{shop.name}</option>
                        ))}
                      </select>
                    </div>
                    <div className="modal-form-group">
                      <label>City</label>
                      <select name="city" value={billForm.city} onChange={handleBillInputChange} disabled={!billForm.shop || citiesLoading}>
                        <option value="">{citiesLoading ? 'Loading cities...' : 'Select a city'}</option>
                        {citiesList.map(city => (
                          <option key={city.id} value={city.name}>{city.name}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                <div className="modal-line-items-section">
                  <div className="modal-line-items-box">
                    <table className="modal-line-items-table">
                      <thead>
                        <tr>
                          <th style={{ width: '40px' }}></th>
                          <th style={{ width: '40px' }}>#</th>
                          <th>CATEGORY</th>
                          <th>DESCRIPTION</th>
                          <th style={{ textAlign: 'right', width: '140px' }}>AMOUNT</th>
                          <th style={{ width: '120px' }}>ACTION</th>
                        </tr>
                      </thead>
                      <tbody>
                        {billForm.lines.map((line, index) => {
                          const isEditing = editingLineId === line.id;
                          return (
                            <tr key={line.id}>
                              <td className="modal-drag-handle">⋮⋮</td>
                              <td>{index + 1}</td>
                              {isEditing ? (
                                <>
                                  <td><input type="text" value={editFormData.category} onChange={(e) => setEditFormData({ ...editFormData, category: e.target.value })} placeholder="Category" autoFocus /></td>
                                  <td><input type="text" value={editFormData.description} onChange={(e) => setEditFormData({ ...editFormData, description: e.target.value })} placeholder="Description" /></td>
                                  <td><input type="number" value={editFormData.amount} onChange={(e) => setEditFormData({ ...editFormData, amount: e.target.value })} placeholder="0.00" style={{ textAlign: 'right' }} /></td>
                                </>
                              ) : (
                                <>
                                  <td><input type="text" value={line.category} onChange={(e) => handleBillLineChange(line.id, 'category', e.target.value)} placeholder="Category" /></td>
                                  <td><input type="text" value={line.description} onChange={(e) => handleBillLineChange(line.id, 'description', e.target.value)} placeholder="Description" /></td>
                                  <td><input type="number" value={line.amount} onChange={(e) => handleBillLineChange(line.id, 'amount', e.target.value)} placeholder="0.00" style={{ textAlign: 'right' }} /></td>
                                </>
                              )}
                              <td>
                                <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                                  {isEditing ? (
                                    <>
                                      <button className="modal-delete-line-btn" style={{ color: '#10b981' }} onClick={() => handleSaveEdit(line.id)} title="Save"><FaCheck /></button>
                                      <button className="modal-delete-line-btn" style={{ color: '#6b7280' }} onClick={handleCancelEdit} title="Cancel"><FaTimes /></button>
                                    </>
                                  ) : (
                                    <>
                                      <button className="modal-delete-line-btn" style={{ color: '#004aad' }} onClick={() => handleStartEdit(line)} title="Edit"><FaEdit /></button>
                                      <button className="modal-delete-line-btn" style={{ color: '#dc2626' }} onClick={() => removeBillLine(line.id)} title="Delete"><RiDeleteBin5Fill /></button>
                                    </>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  <div className="modal-line-items-actions">
                    <div className="modal-action-buttons">
                      <button className="modal-add-line-btn" onClick={addBillLine}>Add lines</button>
                      <button className="modal-clear-lines-btn" onClick={clearAllBillLines}>Clear all lines</button>
                    </div>
                    <div className="modal-total-section"><span>Total</span><strong>£{calculateBillTotal()}</strong></div>
                  </div>
                </div>

                <div className="modal-bottom-section">
                  <div className="modal-form-row">
                    <div className="modal-form-group">
                      <label>Memo</label>
                      <textarea name="memo" value={billForm.memo} onChange={handleBillInputChange} placeholder="Add a memo..." rows={4} />
                    </div>
                    <div className="modal-form-group">
                      <label>Attachment</label>
                      {billForm.attachmentName ? (
                        <div className="attachment-preview-box">

                          {/* ✅ FIXED: Clickable link checks for blob (new upload) or appends backend URL for saved files */}
                          <a
                            href={
                              billForm.attachmentUrl
                                ? (billForm.attachmentUrl.startsWith('blob:')
                                    ? billForm.attachmentUrl
                                    : `${BACKEND_BASE_URL}${billForm.attachmentUrl}`)
                                : '#'
                            }
                            target="_blank"
                            rel="noopener noreferrer"
                            className="attachment-name"
                            style={{ color: '#004aad', textDecoration: 'underline', cursor: 'pointer' }}
                          >
                            {billForm.attachmentName}
                          </a>

                          <div className="attachment-actions">
                            <button className="att-btn replace" onClick={() => billFileInputRef.current?.click()}>Change</button>
                          </div>
                          <input type="file" ref={billFileInputRef} onChange={handleBillAttachmentChange} style={{ display: 'none' }} />
                        </div>
                      ) : (
                        <label className="modal-attachment-box" htmlFor="bill-attachment">
                          <div className="modal-attachment-dropzone">
                            <FaPaperclip className="modal-attach-icon" />
                            <p>Add attachment</p>
                            <span>Max file size: 20 MB</span>
                          </div>
                          <input id="bill-attachment" type="file" onChange={handleBillAttachmentChange} style={{ display: 'none' }} />
                        </label>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="expense-modal-footer">
                <button className="modal-footer-btn modal-cancel-btn" onClick={closeBillModal}>Cancel</button>
                <div className="modal-footer-right">
                  <button className="modal-footer-btn modal-save-btn" onClick={handleSaveBill} disabled={saving}>
                    {saving ? 'Saving...' : (editingRowId ? 'Update' : 'Save')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ✅ NEW: Autofill Modal Render */}
        {showAutofillModal && (
          <AutofillBill
            onClose={() => setShowAutofillModal(false)}
            onSave={({ formData, file, fileUrl }) => {
              setShowAutofillModal(false);

              const totalAmount = formData.lines.reduce((sum, item) => sum + parseFloat(item.amount || 0), 0);
              const newBillForTable = {
                id: Date.now(), // temporary ID
                bill: 'Bill',
                source: 'Autofill',
                supplier: formData.supplier,
                billNo: formData.billNo,
                billDate: formData.billDate,
                category: formData.lines[0]?.category || 'General',
                dueDate: formData.dueDate,
                billAmount: totalAmount,
                billAmountFormatted: `£${totalAmount.toFixed(2)}`,
                status: 'unpaid',
                attachmentName: file?.name || '',
                attachmentUrl: fileUrl || '',
                rawFormData: {
                  supplier: formData.supplier,
                  bill_number: formData.billNo,
                  bill_date: formData.billDate,
                  due_date: formData.dueDate,
                  memo: formData.memo,
                  items: formData.lines,
                  shop: '',
                  city: ''
                }
              };

              // Add immediately to the table
              setBillsData(prev => [newBillForTable, ...prev]);
            }}
          />
        )}

        <div className="bills-table-container">{renderBillsTable()}</div>
        <div className="bills-pagination">
          <button className="pagination-btn" disabled={currentPage === 1} onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}><FaChevronLeft /> Previous</button>
          <button className="pagination-btn" onClick={() => setCurrentPage(prev => prev + 1)}>Next <FaChevronRight /></button>
        </div>
      </div>
    </div>
  );
};

export default Bills;